import * as B from '@blueprintjs/core';
import * as geojson from 'geojson';
import * as I from 'immutable';
import mapboxgl from 'mapbox-gl';
import React from 'react';
import ReactDOM from 'react-dom';

import {NoteForm} from 'app/components/NoteCard/NoteCard';
import {usePushNotification} from 'app/components/Notification';
import {ApiFeatureData} from 'app/modules/Remote/Feature';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {LoggedInUserActions} from 'app/providers/AuthProvider';
import {NotesActions, NotesState} from 'app/stores/NotesStore';
import {recordEvent} from 'app/tools/Analytics';
import * as mapUtils from 'app/utils/mapUtils';

import dialogCs from './AddNoteControl.module.scss';
import MapContent from './MapContent';
import cs from './StyleControl.module.scss';

import {MapControlPosition} from '.';

interface Props {
  map: mapboxgl.Map;
  isMapLoaded: boolean;
  position: MapControlPosition;
  startsGroup?: boolean;

  notesState: NotesState;
  notesActions: NotesActions;

  firebaseToken: string;
  organization: I.ImmutableOf<ApiOrganization>;
  profile: I.ImmutableOf<ApiOrganizationUser>;
  featureCollection: I.ImmutableOf<ApiFeatureCollection>;
  featureData: I.ImmutableListOf<ApiFeatureData> | null;
  imageRefs: mapUtils.MapImageRefs;
  isOverlayVisibleByName: Record<string, boolean>;

  onSelectImageRefs: (imageRefs: mapUtils.MapImageRefs) => unknown;
  setTags: LoggedInUserActions['setOrganizationTags'];
  promptForCoordinates: () => unknown;
  zoomToGeometry: (geometry: geojson.GeoJSON) => unknown;
}

const AddNoteControl: React.FunctionComponent<Props> = ({
  map,
  isMapLoaded,
  position,
  startsGroup,
  notesState,
  notesActions,
  firebaseToken,
  organization,
  profile,
  featureCollection,
  featureData,
  imageRefs,
  isOverlayVisibleByName,
  onSelectImageRefs,
  setTags,
  promptForCoordinates,
  zoomToGeometry,
}) => {
  const controlEl = React.useMemo(() => document.createElement('div'), []);
  const control = React.useMemo(
    () => new mapUtils.PortalControl(controlEl, startsGroup ? cs.groupStart : undefined),
    [controlEl, startsGroup]
  );
  const pushNotification = usePushNotification();

  const composer = notesState.noteComposer;
  const isDialogOpen = !!composer && composer.source !== 'sidebar';
  const isSidebarComposerOpen = composer?.source === 'sidebar';
  const anchor = isDialogOpen ? composer.anchor : null;

  // A note started at a map location (right-click, pixel info) gets its dialog
  // anchored there in a map popup; otherwise the dialog hangs off the control.
  const anchoredEl = React.useMemo(() => document.createElement('div'), []);
  React.useEffect(() => {
    if (!anchor) {
      return;
    }
    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      maxWidth: 'none',
      offset: 12,
      className: dialogCs.anchoredDialog,
    })
      .setLngLat(anchor)
      .setDOMContent(anchoredEl)
      .addTo(map);
    // React applies autoFocus while the node is still detached, so the form
    // opens unfocused and keystrokes would reach the map's hotkeys instead.
    anchoredEl.querySelector('textarea')?.focus();
    return () => {
      popup.remove();
    };
  }, [map, anchor, anchoredEl]);

  // Blueprint's own Escape handling stops working once the user clicks the map
  // to place the note, and it never covered the anchored popup.
  React.useEffect(() => {
    if (!isDialogOpen) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        notesActions.stopComposingNote();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isDialogOpen, notesActions]);

  if (!notesState.mayCreateNotes) {
    return null;
  }

  const form = (
    <NoteForm
      renderCompact
      firebaseToken={firebaseToken}
      organization={organization}
      profile={profile}
      featureCollection={featureCollection}
      featureData={featureData}
      imageRefs={imageRefs}
      notesState={notesState}
      notesActions={notesActions}
      isOverlayVisibleByName={isOverlayVisibleByName}
      onSelectImageRefs={onSelectImageRefs}
      setTags={setTags}
      promptForCoordinates={promptForCoordinates}
      zoomToGeometry={zoomToGeometry}
      autoUpdateLayerAndCursors
      initialText={composer?.text}
      focusNoteOnCreate={false}
      onClose={() => notesActions.stopComposingNote()}
      onNoteCreated={(note) => {
        recordEvent('Created note from scratch', {
          projectId: note.projectId,
          featureCollectionId: note.featureCollectionId,
          noteId: note.id,
          source: composer?.source ?? 'mapControl',
        });
        pushNotification({
          message: 'Note saved',
          autoHideDuration: 6000,
          options: {
            intent: B.Intent.SUCCESS,
            action: {text: 'View', onClick: () => notesActions.focusNote(note.id)},
          },
        });
      }}
    />
  );

  return (
    <React.Fragment>
      <MapContent
        map={map}
        isMapLoaded={isMapLoaded}
        controls={[control]}
        controlPosition={position}
      />
      {anchor && ReactDOM.createPortal(form, anchoredEl)}
      {ReactDOM.createPortal(
        <B.Popover
          isOpen={isDialogOpen && !anchor}
          position={B.Position.LEFT_TOP}
          enforceFocus={false}
          autoFocus={false}
          // Only Escape closes the dialog. Clicks outside land on the map, where
          // the user may be placing the note's location.
          onClose={(e) => {
            if (e?.type === 'keydown') {
              notesActions.stopComposingNote();
            }
          }}
          content={form}
        >
          <B.Tooltip
            content={
              isSidebarComposerOpen ? 'Finish your note in the sidebar first' : 'Add note (N)'
            }
            position={B.Position.LEFT}
            disabled={isDialogOpen}
          >
            <B.AnchorButton
              id="add-note-control"
              icon="annotation"
              className={cs.mapIcon}
              active={isDialogOpen}
              // Starting a second note would replace the first, losing its draft.
              disabled={isSidebarComposerOpen}
              onClick={() =>
                isDialogOpen
                  ? notesActions.stopComposingNote()
                  : notesActions.startComposingNote({source: 'mapControl'})
              }
            />
          </B.Tooltip>
        </B.Popover>,
        controlEl
      )}
    </React.Fragment>
  );
};

export default AddNoteControl;
